import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, MessageSquare, Inbox, Flame, Building2, UserCog, Calendar,
  MapPin, Handshake, BarChart3, Brain, BookOpen, Zap, Settings as SettingsIcon,
  Search, Bell, LogOut, Sparkles, Menu, X, ShieldCheck, FileBarChart, KeyRound, Clock3, Bot, Cloud, Palette, Crown, Megaphone, Sun, Moon, ScanSearch, ChevronRight, WalletCards, Workflow, CreditCard,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API } from "../lib/api";
import { timeAgo, typeBadge } from "../lib/helpers";
import { PageErrorBoundary } from "./PageErrorBoundary";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { section: "ADMIN CONTROL", admin: true },
  { to: "/admin", label: "Control Centre", icon: ShieldCheck, admin: true },
  { to: "/admin/users", label: "User Accounts", icon: UserCog, admin: true },
  { to: "/admin/consents", label: "Consent Registry", icon: ShieldCheck, admin: true },
  { to: "/admin/gods", label: "Gods", icon: Crown, admin: true },
  { to: "/admin/expenses", label: "Live Expenses", icon: WalletCards, admin: true },
  { to: "/leads", label: "Leads", icon: Users },
  { to: "/whatsapp", label: "OpenWA", icon: MessageSquare, admin: true },
  { to: "/whatsapp/inbox", label: "OpenWA Chat", icon: Inbox },
  { to: "/whatsapp/broadcasts", label: "Broadcasts", icon: Megaphone, admin: true },
  { to: "/follow-ups", label: "Follow-ups", icon: Clock3 },
  { to: "/nurturing", label: "Nurturing", icon: Brain },
  { section: "AI AGENTS", admin: true },
  { to: "/ai-agents", label: "Agents Overview", icon: Bot, admin: true },
  { to: "/admin/competitions", label: "Bot Competition", icon: Crown, admin: true },
  { to: "/ai-agents/agent-consultative", label: "Aarav Agent", agentId: "agent-consultative", icon: Bot, admin: true },
  { to: "/ai-agents/agent-insight", label: "Meera Agent", agentId: "agent-insight", icon: Bot, admin: true },
  { to: "/hot-leads", label: "Hot Leads", icon: Flame, admin: true },
  { to: "/projects", label: "Projects", icon: Building2 },
  { to: "/associates", label: "Associates", icon: UserCog },
  { to: "/meetings", label: "Meetings", icon: Calendar },
  { to: "/site-visits", label: "Site Visits", icon: MapPin },
  { to: "/deals", label: "Deals", icon: Handshake },
  { to: "/analytics", label: "Analytics", icon: BarChart3, admin: true },
  { to: "/reports", label: "Monthly Reports", icon: FileBarChart, admin: true },
  { to: "/ai-intelligence", label: "AI Intelligence", icon: Brain, admin: true },
  { to: "/knowledge", label: "AI Knowledge", icon: BookOpen },
  { to: "/conversation-lab", label: "Conversation Lab", icon: ScanSearch, admin: true },
  { to: "/playground", label: "AI Playground", icon: Sparkles, admin: true },
  { to: "/digital-cards", label: "Digital Cards", icon: CreditCard },
  { to: "/automations", label: "Automations", icon: Zap, admin: true },
  { to: "/flows", label: "Visual Flows", icon: Workflow, admin: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, admin: true },
  { to: "/appearance", label: "Appearance", icon: Palette, admin: true },
  { to: "/account", label: "My Account", icon: KeyRound },
];

function Logo({brand}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative h-9 w-9 flex items-center justify-center">
        <div className="absolute inset-0 rotate-45 rounded-md shadow-lg" style={{background:brand.primary_color}} />
        <span className="relative text-slate-950 font-extrabold text-sm">MB</span>
      </div>
      <div className="leading-tight">
        <div className="font-heading font-extrabold text-lg tracking-tight text-white">{brand.crm_name}</div>
        <div className="caption-mono !text-[9px] !tracking-[0.2em]">{brand.tagline}</div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [brand, setBrand] = useState({crm_name:"MB NEXUS",tagline:"RAV SALES OS",primary_color:"#f59e0b",secondary_color:"#7c3aed",background_color:"#0b1120",surface_color:"#111827",text_color:"#f8fafc",muted_text_color:"#94a3b8",border_color:"#29344a",card_gradient:"linear-gradient(145deg, #171d2e, #0e121f)",button_gradient:"linear-gradient(135deg, #fbbf24, #f97316)",font_family:"Outfit",corner_style:"rounded",density:"comfortable",sidebar_style:"solid",theme_mode:"dark"});
  const [theme, setTheme] = useState(() => localStorage.getItem("mb_theme") || "dark");
  const [agentNames, setAgentNames] = useState({});
  const [approvalCount, setApprovalCount] = useState(0);
  useEffect(() => {
    document.body.classList.toggle("notification-sheet-open", showNotif);
    return () => document.body.classList.remove("notification-sheet-open");
  }, [showNotif]);

  useEffect(() => {
    API.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    API.get("/branding").then((r) => { setBrand(r.data); if (!localStorage.getItem("mb_theme")) setTheme(r.data.theme_mode === "light" ? "light" : "dark"); }).catch(() => {});
    if (isAdmin) {
      API.get("/sales-agents").then((r) => setAgentNames(Object.fromEntries(r.data.map(a => [a.id, a.name])))).catch(() => {});
      const refreshApprovals = () => API.get("/cloud-approvals").then((r) => setApprovalCount(r.data.count || 0)).catch(() => {});
      refreshApprovals();
      const timer = setInterval(refreshApprovals, 30000);
      return () => clearInterval(timer);
    }
  }, [isAdmin]);

  const items = NAV.filter((n) => !n.admin || isAdmin).map(n => n.agentId && agentNames[n.agentId] ? {...n, label:`${agentNames[n.agentId]} Agent`} : n);
  const unread = notifs.filter((n) => !n.read).length;

  const notificationRoute = (notification) => {
    if (notification.lead_id) return `/leads/${notification.lead_id}`;
    if (notification.associate_id) return `/associates/${notification.associate_id}`;
    const title = (notification.title || "").toLowerCase();
    if (title.includes("report")) return "/reports";
    if (title.includes("project")) return "/admin";
    return isAdmin ? "/admin" : "/";
  };

  const openNotification = async (notification) => {
    if (!notification.read) {
      setNotifs((current) => current.map((item) => item.id === notification.id ? { ...item, read: true } : item));
      API.post(`/notifications/${notification.id}/read`).catch(() => {});
    }
    setShowNotif(false);
    nav(notificationRoute(notification));
  };

  const runSearch = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults(null); return; }
    try {
      const { data } = await API.get(`/search?q=${encodeURIComponent(val)}`);
      setResults(data);
    } catch { setResults(null); }
  };

  const SidebarInner = (
    <>
      <div className="px-5 py-5"><Logo brand={brand} /></div>
      <div className="px-3 mb-3">
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${isAdmin ? "bg-amber-500/10 border-amber-500/20 text-amber-400" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"}`}>
          <ShieldCheck className="h-3.5 w-3.5" />
          <span className="font-semibold uppercase tracking-wide" data-testid="role-badge">{isAdmin ? "Admin" : "Associate"} Mode</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 space-y-0.5 pb-6">
        {items.map((n) => n.section ? <div key={n.section} className="px-3 pt-5 pb-1 text-[10px] tracking-[0.18em] text-purple-400 font-semibold">{n.section}</div> : (
          <NavLink
            key={n.to} to={n.to} end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}-link`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border-l-2 border-transparent"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px]" /> <span className="flex-1">{n.label}</span>{n.approvalBadge&&approvalCount>0&&<span className="min-w-5 h-5 px-1 rounded-full bg-purple-600 text-white text-[10px] flex items-center justify-center">{approvalCount}</span>}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <div className="app-shell min-h-screen flex" data-theme={theme} data-density={brand.density} data-corners={brand.corner_style} data-sidebar={brand.sidebar_style} style={{"--brand-color":brand.primary_color,"--brand-secondary":brand.secondary_color,"--app-bg":brand.background_color,"--app-surface":brand.surface_color,"--app-text":brand.text_color,"--app-muted":brand.muted_text_color,"--app-border":brand.border_color,"--card-gradient":brand.card_gradient,"--button-gradient":brand.button_gradient,"--heading-font":brand.font_family==="System"?"system-ui":brand.font_family}}>
      {/* Desktop sidebar */}
      <aside className="app-sidebar app-sidebar--desktop hidden lg:flex flex-col w-[276px] border-r border-slate-800/80">
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="app-sidebar absolute left-0 top-0 h-full w-72 max-w-[86vw] flex flex-col border-r border-slate-800">
            <button className="absolute right-3 top-4 text-slate-400" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="app-content flex-1 flex flex-col min-w-0 lg:ml-[276px]">
        {/* Topbar */}
        <header className="app-topbar sticky top-0 z-40 bg-[#0B1120]/80 backdrop-blur-xl border-b border-slate-800/80">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                data-testid="global-search-input"
                value={q} onChange={(e) => runSearch(e.target.value)}
                placeholder="Search leads, projects, associates…"
                className="w-full bg-slate-900/70 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/40"
              />
              {results && (
                <div className="absolute mt-2 w-full bg-[#111827] border border-slate-800 rounded-xl shadow-2xl p-2 max-h-80 overflow-y-auto z-50" data-testid="search-results">
                  {results.leads?.map((l) => (
                    <div key={l.id} onClick={() => { nav(`/leads/${l.id}`); setResults(null); setQ(""); }}
                      className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-800 cursor-pointer">
                      <span className="text-sm text-slate-200">{l.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadge(l.lead_type)}`}>{l.lead_type}</span>
                    </div>
                  ))}
                  {results.projects?.map((p) => (
                    <div key={p.id} onClick={() => { nav(`/projects`); setResults(null); setQ(""); }}
                      className="px-3 py-2 rounded-lg hover:bg-slate-800 cursor-pointer text-sm text-slate-300">🏢 {p.name}</div>
                  ))}
                  {(!results.leads?.length && !results.projects?.length && !results.associates?.length) && (
                    <div className="px-3 py-3 text-sm text-slate-500">No results</div>
                  )}
                </div>
              )}
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button aria-label="Toggle light and dark mode" onClick={() => setTheme((current) => { const next=current === "dark" ? "light" : "dark"; localStorage.setItem("mb_theme", next); return next; })} className="topbar-icon">{theme === "dark" ? <Sun className="h-[18px] w-[18px]"/> : <Moon className="h-[18px] w-[18px]"/>}</button>
              <div className="relative">
                <button data-testid="notifications-btn" onClick={() => setShowNotif((s) => !s)}
                  aria-expanded={showNotif} aria-haspopup="dialog" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
                  className="relative h-9 w-9 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-300">
                  <Bell className="h-[18px] w-[18px]" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">{unread}</span>}
                </button>
              </div>
              <div className="flex items-center gap-2.5 pl-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-slate-950 font-bold text-sm">
                  {(user?.name || "U").charAt(0)}
                </div>
                <div className="hidden sm:block leading-tight">
                  <div className="text-sm font-medium text-slate-200">{user?.name}</div>
                  <div className="text-[11px] text-slate-500">{user?.email}</div>
                </div>
                <button data-testid="logout-btn" onClick={logout} className="ml-1 h-9 w-9 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-red-400">
                  <LogOut className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="app-main flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto">
          <PageErrorBoundary key={location.pathname}><Outlet /></PageErrorBoundary>
        </main>
      </div>
      {showNotif && createPortal(
        <div className="notification-layer" role="presentation">
          <button type="button" className="notification-backdrop" onClick={() => setShowNotif(false)} aria-label="Close notifications" />
          <section className="notification-sheet" role="dialog" aria-modal="true" aria-label="Notifications">
            <header className="notification-sheet__header">
              <div><span>Activity centre</span><strong>Notifications</strong></div>
              <div className="notification-sheet__actions">
                {unread > 0 && <span>{unread} unread</span>}
                <button type="button" onClick={() => setShowNotif(false)} aria-label="Close notifications"><X className="h-5 w-5" /></button>
              </div>
            </header>
            <div className="notification-sheet__list">
              {notifs.length === 0 && <div className="notification-empty"><Bell className="h-6 w-6" /><span>No notifications yet</span></div>}
              {notifs.map((n) => (
                <button type="button" key={n.id} onClick={() => openNotification(n)} className={`notification-item ${n.read ? "is-read" : "is-unread"}`}>
                  <i aria-hidden="true" />
                  <div><strong>{n.title}</strong><p>{n.body}</p><time>{timeAgo(n.created_at)}</time></div>
                  <ChevronRight className="h-4 w-4" />
                </button>
              ))}
            </div>
          </section>
        </div>,
        document.body
      )}
    </div>
  );
}
